// ============================================================
// Pointer pixels to battlefield scene units.
//
// The battlefield's SVG scales its fixed viewBox (SCENE_VIEW) to the panel
// with `meet`, so a screen pixel is not a scene unit: the scene is scaled
// uniformly and letterboxed on the long axis. Every pointer gesture on the
// map (build, measure, drag, heading, deployment) converts through this one
// mapping, or its marker lags the pointer on a panel that is not 10:7.
// ============================================================
import { SCENE_VIEW } from '../engine/battlefield/projection';

interface RectLike {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SceneTarget {
  ownerSVGElement?: { getBoundingClientRect(): RectLike } | null;
  getBoundingClientRect(): RectLike;
}

/** The slice of a pointer event the conversion reads. */
export interface ScenePointerEvent {
  clientX: number;
  clientY: number;
  currentTarget: SceneTarget;
}

/**
 * The scene point under a pointer event, in SCENE_VIEW units.
 *
 * The event's target may be the <svg> itself or any element inside it; the
 * owning <svg>'s box is what the viewBox was fitted to.
 */
export function pointerToScene(e: ScenePointerEvent): { x: number; y: number } {
  const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
  const rect = svg.getBoundingClientRect();
  const scale = Math.min(rect.width / SCENE_VIEW.width, rect.height / SCENE_VIEW.height);
  return {
    x: (e.clientX - (rect.x + (rect.width - SCENE_VIEW.width * scale) / 2)) / scale,
    y: (e.clientY - (rect.y + (rect.height - SCENE_VIEW.height * scale) / 2)) / scale,
  };
}
